import { useCallback, useEffect, useMemo, useState } from 'react';
import type { User } from 'firebase/auth';
import { Boton, Panel } from '../ui';
import { MesaCortes, MesaDetalleNumerico, MesaDimensiones } from './MesaThermometers';
import { MesaOutbox, type OutboxItem } from './MesaOutbox';
import { Kpi } from './MesaKpi';
import { CuentaMesa, MarcaMesa, PuertaMesa, type AuthedFetch } from './acceso';
import './mesa.css';

type Aggregate = {
  n: number;
  promedioPorDimension: Record<string, number>;
  indiceInnovacionPromedio: number;
  pctPorNivel: Record<string, Record<string, number>>;
  cortes: Record<string, null | Record<string, number>>;
};

type Rol = 'viewer' | 'operator';

const hora = new Intl.DateTimeFormat('es-AR', {
  hour: '2-digit',
  minute: '2-digit',
  second: '2-digit',
});

export function Mesa() {
  return (
    <PuertaMesa producto="Panel operativo de la autoevaluación de madurez IA">
      {(s) => <TableroMesa user={s.user} authedFetch={s.authedFetch} />}
    </PuertaMesa>
  );
}

function TableroMesa({ user, authedFetch }: { user: User; authedFetch: AuthedFetch }) {
  const [agg, setAgg] = useState<Aggregate | null>(null);
  const [outbox, setOutbox] = useState<OutboxItem[]>([]);
  const [rol, setRol] = useState<Rol | null>(null);
  const [cargando, setCargando] = useState(true);
  const [ocupado, setOcupado] = useState<null | 'refresh' | 'csv' | 'outbox'>(null);
  const [actualizado, setActualizado] = useState<Date | null>(null);
  const [nota, setNota] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const refresh = useCallback(async () => {
    setError(null);
    setNota(null);
    setOcupado('refresh');
    try {
      const [a, o] = await Promise.all([
        authedFetch('/api/admin/aggregate').then((r) => r.json() as Promise<Aggregate>),
        authedFetch('/api/admin/outbox').then(
          (r) => r.json() as Promise<{ items: OutboxItem[] }>,
        ),
      ]);
      setAgg(a);
      setOutbox(Array.isArray(o.items) ? o.items : []);
      setActualizado(new Date());
      const me = await authedFetch('/api/admin/me')
        .then((r) => r.json() as Promise<{ role?: Rol }>)
        .catch(() => null);
      if (me?.role) setRol(me.role);
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setOcupado(null);
      setCargando(false);
    }
  }, [authedFetch]);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  const enCola = useMemo(
    () => outbox.filter((i) => i.status === 'pending' || i.status === 'failed').length,
    [outbox],
  );
  const enviados = useMemo(() => outbox.filter((i) => i.status === 'sent').length, [outbox]);
  const puedeProcesar = rol !== 'viewer';

  const exportarCsv = async () => {
    setError(null);
    setNota(null);
    setOcupado('csv');
    try {
      const res = await authedFetch('/api/admin/export.csv');
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = 'cessi-autoeval.csv';
      a.click();
      URL.revokeObjectURL(url);
      setNota('Export CSV §14.2 descargado.');
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setOcupado(null);
    }
  };

  const procesarOutbox = async () => {
    setError(null);
    setNota(null);
    setOcupado('outbox');
    try {
      const res = await authedFetch('/api/admin/outbox/process', { method: 'POST' });
      const { sent } = (await res.json()) as { sent?: number };
      const resumen = sent
        ? `${sent} email${sent === 1 ? '' : 's'} enviado${sent === 1 ? '' : 's'}.`
        : 'No había emails para enviar (o SMTP no está configurado: quedan en “manual”).';
      await refresh();
      setNota(resumen);
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setOcupado(null);
    }
  };

  const sinDatos = agg !== null && agg.n === 0;

  return (
    <div className="shell shell--mesa">
      <div className="mesa-topbar">
        <MarcaMesa sub="Autoevaluación de madurez IA · datos agregados" />
        <CuentaMesa user={user} rol={rol} />
      </div>

      <div className="mesa-toolbar">
        <p className="mesa-toolbar__meta">
          <span className="mesa-pulso" aria-hidden="true" />
          {actualizado
            ? `Actualizado ${hora.format(actualizado)}`
            : cargando
              ? 'Cargando datos…'
              : 'Sin datos cargados'}
        </p>
        <div className="mesa-toolbar__acciones">
          <Boton variante="secundario" onClick={() => void refresh()} disabled={ocupado !== null}>
            {ocupado === 'refresh' ? 'Actualizando…' : 'Actualizar'}
          </Boton>
          <Boton variante="secundario" onClick={() => void exportarCsv()} disabled={ocupado !== null}>
            {ocupado === 'csv' ? 'Generando…' : 'Export CSV §14.2'}
          </Boton>
          <Boton
            variante="secundario"
            onClick={() => void procesarOutbox()}
            disabled={ocupado !== null || !puedeProcesar}
            title={puedeProcesar ? undefined : 'Requiere rol operador'}
          >
            {ocupado === 'outbox' ? 'Procesando…' : 'Procesar outbox'}
          </Boton>
        </div>
      </div>

      {error && (
        <div className="mesa-nota mesa-nota--error" role="alert">
          <p className="mesa-nota__cuerpo">
            <strong>Algo no salió bien</strong>
            {error}
          </p>
        </div>
      )}
      {nota && !error && (
        <div className="mesa-nota" role="status">
          <p className="mesa-nota__cuerpo">{nota}</p>
        </div>
      )}

      <ul className="mesa-kpis">
        <Kpi
          label="Organizaciones"
          valor={agg ? String(agg.n) : '—'}
          pie="Respuestas canónicas (una por organización)"
        />
        <Kpi
          label="Índice de innovación"
          valor={agg ? agg.indiceInnovacionPromedio.toFixed(1) : '—'}
          unidad="/ 4"
          pie="Promedio de Innovación con IA sobre las canónicas"
          variante="acento"
        />
        <Kpi
          label="Emails en cola"
          valor={agg ? String(enCola) : '—'}
          pie="Pendientes o fallidos, listos para procesar"
          variante={agg && enCola > 0 ? 'atencion' : undefined}
        />
        <Kpi
          label="Emails enviados"
          valor={agg ? String(enviados) : '—'}
          pie="Últimos 100 registros de la cola"
        />
      </ul>

      <div className="mesa-grid mesa-grid--principal">
        <Panel
          titulo="Madurez por dimensión"
          subtitulo="Promedio y distribución de niveles · escala 1 a 4"
        >
          {cargando && !agg ? (
            <div aria-busy="true" aria-label="Cargando dimensiones">
              <div className="mesa-skel" style={{ height: 56 }} />
              <div className="mesa-skel" style={{ height: 56 }} />
              <div className="mesa-skel" style={{ height: 56 }} />
              <div className="mesa-skel" style={{ height: 56 }} />
              <div className="mesa-skel" style={{ height: 56 }} />
            </div>
          ) : sinDatos ? (
            <div className="mesa-vacio">
              <p className="mesa-vacio__titulo">Todavía no hay autoevaluaciones canónicas</p>
              <p className="mesa-vacio__texto">
                Cuando la primera organización complete el cuestionario, acá vas a ver el promedio
                por dimensión y el reparto de niveles.
              </p>
            </div>
          ) : agg ? (
            <>
              <MesaDimensiones
                promedios={agg.promedioPorDimension}
                pctPorNivel={agg.pctPorNivel}
              />
              <MesaDetalleNumerico pctPorNivel={agg.pctPorNivel} cortes={agg.cortes} />
            </>
          ) : (
            <div className="mesa-vacio">
              <p className="mesa-vacio__titulo">No pudimos traer el agregado</p>
              <p className="mesa-vacio__texto">Probá con “Actualizar”.</p>
            </div>
          )}
        </Panel>

        <Panel titulo="Cortes" subtitulo="Se ocultan los grupos con menos de 5 organizaciones">
          {cargando && !agg ? (
            <div aria-busy="true" aria-label="Cargando cortes">
              <div className="mesa-skel" style={{ height: 72 }} />
              <div className="mesa-skel" style={{ height: 72 }} />
              <div className="mesa-skel" style={{ height: 72 }} />
            </div>
          ) : agg && !sinDatos ? (
            <MesaCortes cortes={agg.cortes} total={agg.n} />
          ) : (
            <div className="mesa-vacio">
              <p className="mesa-vacio__titulo">Sin cortes disponibles</p>
              <p className="mesa-vacio__texto">
                Los cortes por modelo de negocio, tamaño y geografía aparecen cuando hay volumen
                suficiente.
              </p>
            </div>
          )}
        </Panel>
      </div>

      <div className="mesa-grid" style={{ marginTop: 16 }}>
        <Panel titulo="Cola de emails" subtitulo="Últimos 100 envíos de perfiles a las organizaciones">
          {cargando && outbox.length === 0 ? (
            <div aria-busy="true" aria-label="Cargando cola de emails">
              <div className="mesa-skel" style={{ height: 44 }} />
              <div className="mesa-skel" style={{ height: 44 }} />
              <div className="mesa-skel" style={{ height: 44 }} />
            </div>
          ) : error && outbox.length === 0 ? (
            <div className="mesa-vacio">
              <p className="mesa-vacio__titulo">No pudimos cargar la cola</p>
              <p className="mesa-vacio__texto">Probá con “Actualizar”.</p>
            </div>
          ) : (
            <MesaOutbox items={outbox} />
          )}
        </Panel>
      </div>
    </div>
  );
}
